import { useNavigate } from "react-router-dom";
import styles from "./StaffList.module.css";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton } from "../../../components";
import { useSelector } from "react-redux";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import { FaDownload } from "react-icons/fa";

function StaffList() {
  const navigate = useNavigate();
  // const location = useLocation();
  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const schoolId = user?.client_data[0].school_id || "";

  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Modal state for success/error messages
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // Pagination metadata from API
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  // Pagination for MRT table
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  // Column filter state
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFiltersRef = useRef([]);

  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  const handleAddStaff = () => {
    navigate("/user/staffSetup");
  };

  // Fetch staff list from API
  const fetchStaffList = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = columnFiltersRef.current;
      const nameFilter = filters.find((f) => f.id === "name")?.value || "";
      const response = await AxiosInstance.post("/school/staff/list", {
        id: schoolId,
        search: nameFilter,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      if (response?.data?.status) {
        const staffData = (response?.data?.data || []).map((staff) => ({
          ...staff,
          gender: staff.gender
            ? staff.gender.charAt(0).toUpperCase() +
            staff.gender.slice(1).toLowerCase()
            : "",
        }));
        setStaffList(staffData);

        if (response?.data?.pagination) {
          setPaginationMeta(response.data.pagination);
        }
      } else {
        throw new Error(
          response?.data?.message || "Failed to fetch staff list",
        );
      }
    } catch (err) {
      console.error("Error fetching staff list:", err);
      setError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
      setStaffList([]);
      setPaginationMeta({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [schoolId, apiSearch, pagination.pageIndex, pagination.pageSize]);

  // Convert array of objects to CSV string
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const excludeKeys = [
      "id",
      "user_id",
      "created_by",
      "updated_by",
      "created_dt",
      "updated_dt",
    ];

    const headers = Object.keys(data[0]).filter(
      (key) => !excludeKeys.includes(key),
    );

    const headerRow = headers
      .map((h) => h.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
      .map((h) => `"${h}"`)
      .join(",");

    const rows = data.map((row) =>
      headers
        .map((key) => {
          let val = row[key] ?? "";
          if (key === "isActive") {
            val = val === 1 ? "Active" : "Inactive";
          }
          const escaped = String(val).replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(","),
    );

    return [headerRow, ...rows].join("\n");
  };

  // Trigger CSV file download in the browser
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download all staff data as CSV
  const handleDownload = async () => {
    try {
      setDownloadLoading(true);

     

      const response = await AxiosInstance.post("/school/staff/list", {
        id: schoolId,
        search: apiSearch.trim(),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

        if (response.data?.status && response.data.data) {
          const staffData = response.data.data.map((staff) => ({
          ...staff,
          gender: staff.gender
            ? staff.gender.charAt(0).toUpperCase() +
            staff.gender.slice(1).toLowerCase()
            : "",
        }));

        if (staffData.length === 0) {
          setModalState({
            open: true,
            type: "success",
            message: response.data?.message || "No data available to download.",
            redirectPath: "",
          });
          return;
        }

        const csvContent = convertToCSV(staffData);
        downloadCSV(csvContent, "STAFF_LIST.csv");
      } else {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || "No data available to download.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error downloading staff list:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download staff list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDownloadLoading(false);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      const isFilterInput =
        e.target.tagName === "INPUT" &&
        (e.target.closest("thead") ||
          e.target.closest(".MuiTableHead-root") ||
          e.target.closest('[class*="MRT_TableHeadCell"]') ||
          e.target.closest('[class*="Header"]'));

      if (isFilterInput) {
        e.preventDefault();
        const latestFilters = columnFiltersRef.current;

        setPagination((prev) => {
          if (prev.pageIndex === 0) {
            fetchStaffList(latestFilters);
            return prev;
          }
          return { ...prev, pageIndex: 0 };
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [fetchStaffList]);
  // Fetch data when pagination or search changes
  useEffect(() => {
    if (schoolId) {
      fetchStaffList();
    }
  }, [
    schoolId,
    apiSearch,
    pagination.pageIndex,
    pagination.pageSize,
    fetchStaffList,
  ]);

  // Handle view staff details
  const handleViewStaff = useCallback(
    (row) => {
      navigate("/user/staffSetup", {
        state: { staffId: row.id, staffData: row },
      });
    },
    [navigate],
  );

  // Get status badge component
  const getStatusBadge = (value) => {
    return (
      <span
        style={{
          backgroundColor: value === 1 ? "#28a745" : "#dc3545",
          color: "#fff",
          padding: "4px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
          display: "inline-block",
        }}
      >
        {value === 1 ? "Active" : "Inactive"}
      </span>
    );
  };

  // Table columns configuration for CustomMRTTable
  const columns = useMemo(
    () => [
      {
        accessorKey: "staff_id",
        header: "Staff ID",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => row.original.staff_id || "-",
      },
      {
        accessorKey: "name",
        header: "Staff Name",
        enableSorting: true,
        enableColumnFilter: true,
        size: 180,
        Cell: ({ row }) => row.original.name || "-",
      },
      {
        accessorKey: "email",
        header: "Email Address",
        enableSorting: true,
        enableColumnFilter: false,
        size: 250,
        Cell: ({ row }) => row.original.email || "-",
      },
      {
        accessorKey: "staff_type",
        header: "Staff Type",
        enableSorting: true,
        enableColumnFilter: false,
        size: 250,
        Cell: ({ row }) => row.original.staff_type || "-",
      },
      {
        accessorKey: "mobile_no",
        header: "Mobile Number",
        enableSorting: true,
        enableColumnFilter: false,
        size: 130,
        Cell: ({ row }) => row.original.mobile_no || "-",
      },
      {
        accessorKey: "gender",
        header: "Gender",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => row.original.gender || "-",
      },
      {
        accessorKey: "subject_name",
        header: "Courses",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => row.original.subject_name || "-",
      },
      {
        accessorKey: "isActive",
        header: "STATUS",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        align: "center",
        Cell: ({ row }) => getStatusBadge(row.original.isActive),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleViewStaff(row.original)}
            title="View Staff"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#007bff",
              fontSize: "18px",
            }}
          >
            <i className="fa-solid fa-eye" />
          </button>
        ),
      },
    ],
    [handleViewStaff],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Staff List</h1>
        <div className={styles.headerActions}>
          <CustomButton
            type="button"
            variant="contained"
            fullWidth={false}
            onClick={handleAddStaff}
          >
            ADD STAFF
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={`${styles.downloadButton} ms-2`}
            fullWidth={false}
            onClick={handleDownload}
            disabled={downloadLoading}
          >
            <FaDownload style={{ marginRight: "8px" }} />
            {downloadLoading
              ? "Downloading..."
              : translations.labels?.downloadButton || "Download"}
          </CustomButton>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={fetchStaffList} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )}

      {/* Modal for download feedback */}
      {modalState.open && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div
              className={
                modalState.type === "error"
                  ? styles.modalError
                  : styles.modalSuccess
              }
            >
              <i
                className={
                  modalState.type === "error"
                    ? "fa-solid fa-circle-exclamation"
                    : "fa-solid fa-circle-check"
                }
                style={{ fontSize: "24px", marginRight: "8px" }}
              ></i>
              {modalState.message}
            </div>
            <button
              className={styles.modalCloseBtn}
              onClick={() =>
                setModalState({
                  open: false,
                  type: "",
                  message: "",
                  redirectPath: "",
                })
              }
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={staffList}
        loading={loading}
        pagination={pagination}
        manualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        enableColumnFilters={true}
        rowCount={paginationMeta.total}
        onPaginationChange={setPagination}
        enableSearch={false}
      // searchInput={searchInput}
      // placeholder={translations.labels?.searchName || "Search by name..."}
      // setSearchInput={setSearchInput}
      // onSearchEnter={(value) => {
      //   setApiSearch(value);
      //   setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      // }}
      // enableFiltering={false}
      />
    </div>
  );
}

export default StaffList;
